import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2, ChevronDown, ChevronRight, Ruler, FileText, Package, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  getConfig, getQuote, createQuote, updateQuote,
  addItem, updateItem, deleteItem, addSupplier
} from "@/lib/api";
import { computeTotals, formatCurrency, FIXED_VAT_RATE, computeActionRate, loadPricingSettings, getPricing, CALL_OUT_RATE, isInCongestionZone, CONGESTION_CHARGE, HOURS_PER_DAY } from "@/lib/calculations";
import FileUploadZone from "@/components/custom/FileUploadZone";
import MeasurementRow from "@/components/custom/MeasurementRow";
import { useIsMobile } from "@/components/custom/AppShell";
import { AiQuoteGenerator, AiRewriteButton, ServiceRewriteButton } from "@/components/custom/AiFeatures";
import { SupplierSelect } from "@/components/custom/SupplierSelect";

const emptyDraft = {
  client_type: "Private",
  site_address: "",
  labour_hours: 0,
  vat_enabled: true,
  status: "Not Started",
  start_date: "",
  materials_status: "Not Started",
  job_notes: "",
  actions: [],
  measurements: [],
  job_details: "",
  parking_type: "",
  parking_hours: 0,
  parking_rate: 0,
  materials_markup: 0,
  works_type: "",
  congestion_days: 0,
  additional_terms: "",
  access_method: "",
  access_branch_address: "",
  access_occupier_phone: "",
  access_notes: "",
};

export default function QuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const isMobile = useIsMobile();

  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [quoteId, setQuoteId] = useState(id || null);
  const [quoteVisitNotes, setQuoteVisitNotes] = useState("");
  const [editingRate, setEditingRate] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const autoCreated = useRef(false);

  const [pricingLoaded, setPricingLoaded] = useState(false);

  useEffect(() => {
    getConfig().then(setConfig).catch(() => toast.error("Failed to load config"));
    loadPricingSettings().then(() => setPricingLoaded(true));
  }, []);

  useEffect(() => {
    if (!id && !autoCreated.current) {
      autoCreated.current = true;
      createQuote(emptyDraft).then((result) => {
        setQuoteId(result.id);
        navigate(`/quotes/${result.id}/edit`, { replace: true });
      }).catch(() => toast.error("Failed to initialise quote"));
    }
  }, [id, navigate]);

  const loadQuote = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getQuote(id);
      setDraft({
        client_type: data.client_type,
        site_address: data.site_address || "",
        labour_hours: data.labour_hours,
        vat_enabled: data.vat_enabled,
        actions: data.actions || [],
        measurements: (data.measurements || []).map(m => m._uid ? m : { ...m, _uid: `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }),
        status: data.status,
        start_date: data.start_date || "",
        materials_status: data.materials_status || "Not Started",
        job_notes: data.job_notes || "",
        job_details: data.job_details || "",
        parking_type: data.parking_type || "",
        parking_hours: data.parking_hours || 0,
        parking_rate: data.parking_rate || 0,
        materials_markup: data.materials_markup || 0,
        works_type: data.works_type || "",
        congestion_days: data.congestion_days || 0,
        additional_terms: data.additional_terms || "",
        access_method: data.access_method || "",
        access_branch_address: data.access_branch_address || "",
        access_occupier_phone: data.access_occupier_phone || "",
        access_notes: data.access_notes || "",
        tenant_phone: data.tenant_phone || "",
      });
      setItems(data.items || []);
      setMedia(data.media || []);
      setQuoteVisitNotes(data.quote_visit_notes || "");
    } catch {
      toast.error("Failed to load quote");
    }
  }, [id]);

  useEffect(() => { if (id) loadQuote(); }, [loadQuote, id]);

  const totals = useMemo(() => {
    return computeTotals(draft, items);
  }, [draft, items]);

  useEffect(() => {
    const totalHours = draft.actions.reduce((s, a) => {
      if (typeof a === "object" && !a.is_sub) return s + (a.hours || 0);
      return s;
    }, 0);
    if (totalHours !== draft.labour_hours) {
      setDraft(prev => ({ ...prev, labour_hours: totalHours }));
    }
  }, [draft.actions]);

  // Auto-detect congestion charge zone from address
  useEffect(() => {
    const inZone = isInCongestionZone(draft.site_address);
    if (inZone && draft.congestion_days === 0 && draft.labour_hours > 0) {
      // Auto-set congestion days based on labour days (round up)
      const days = Math.max(1, Math.ceil(draft.labour_hours / HOURS_PER_DAY));
      setDraft(prev => ({ ...prev, congestion_days: days }));
    }
  }, [draft.site_address, draft.labour_hours]);


  const updateDraft = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  // Applies the AI quote-generator response to the current draft. Shared between the
  // full "AI Generate" and "Generate single action" flows — both are additive (append).
  const applyAiResult = useCallback(async (result, { singleAction } = {}) => {
    if (window._aiApplying) return;
    window._aiApplying = true;
    try {
      const services = result.services || [];
      // Single-action mode hard-cap on the client side (defence in depth).
      const trimmed = singleAction ? services.slice(0, 1) : services;
      const existingCount = draft.actions.length;
      const newActions = trimmed.map(s => ({
        title: s.title,
        description: s.description,
        hours: s.hours || 0,
        price: 0,
        is_sub: false,
      }));
      updateDraft("actions", [...draft.actions, ...newActions]);
      for (let si = 0; si < trimmed.length; si++) {
        const service = trimmed[si];
        const actionIndex = existingCount + si;
        for (const m of (service.materials || [])) {
          try {
            const newItem = await addItem(quoteId || id, {
              supplier: m.supplier || "Toolstation",
              location: "Click and Collect",
              item_name: m.item_name || "",
              sku: m.sku || "",
              unit_price: m.unit_price || 0,
              qty: m.qty || 1,
              product_url: "",
              ordered: false,
              action_index: actionIndex,
            });
            setItems(prev => [...prev, newItem]);
          } catch (err) { console.error(err); }
        }
      }
      // Apply optional parking + congestion from the panel
      const draftPatch = {};
      if (result.parking) {
        draftPatch.parking_type = result.parking.type || draft.parking_type || "Pay by Phone";
        draftPatch.parking_rate = result.parking.rate || draft.parking_rate || 0;
        draftPatch.parking_hours = result.parking.hours || draft.parking_hours || 0;
      }
      if (typeof result.congestion_days === "number" && result.congestion_days > 0) {
        draftPatch.congestion_days = result.congestion_days;
      }
      if (Object.keys(draftPatch).length > 0) {
        setDraft(prev => ({ ...prev, ...draftPatch }));
      }
    } finally { window._aiApplying = false; }
  }, [draft.actions, draft.parking_type, draft.parking_rate, draft.parking_hours, quoteId, id]);

  const handleClientTypeChange = (newType) => {
    const updatedActions = draft.actions.map(a => {
      if (typeof a !== "object" || a.is_sub) return a;
      return { ...a, price: computeActionRate(a.hours || 0, newType) };
    });
    setDraft(prev => ({ ...prev, client_type: newType, actions: updatedActions }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const targetId = quoteId || id;
      if (targetId) {
        const actionsWithPrices = draft.actions.map(a => {
          if (typeof a !== "object" || a.is_sub) return a;
          if (a.price != null && a.price > 0) return a;
          return { ...a, price: computeActionRate(a.hours || 0, draft.client_type, draft.locked_hourly_rate) };
        });
        const saveData = { ...draft, actions: actionsWithPrices, quote_started: true };
        // Auto-advance status
        if (draft.status === "Not Started") {
          saveData.status = "Awaiting Quote";
        }
        // WO no-visit: Quote Accepted → save as Job Signed (ready to send invoice)
        if (draft.works_type === "Works Order" && draft.status === "Quote Accepted") {
          saveData.status = "Job Signed";
        }
        await updateQuote(targetId, saveData);
        toast.success(draft.works_type === "Works Order" ? "Invoice saved" : "Quote saved");
        navigate(`/quotes/${targetId}`, { replace: true });
      }
    } catch {
      toast.error("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };

  const addingItem = useRef(false);
  // Per-scope memory of the last material the user added in THIS editing session.
  // Key = action_index (number, or -1 for General Materials). Value = item id.
  // We store only the id and look up the live supplier from `items` at add-time —
  // that way if the user changes the supplier on that material before adding the next
  // one, the next one auto-fills with the updated value (per spec test case 2).
  // If the user edits an EARLIER material instead, the ref doesn't move, so older
  // edits never leak into the new-material default (spec test case 3).
  // The ref starts empty on mount, satisfying "first add of a fresh session = blank".
  const lastAddedIdByScope = useRef({});
  const handleAddItem = async (actionIndex = -1) => {
    if (addingItem.current) return;
    addingItem.current = true;
    try {
      const lastId = lastAddedIdByScope.current[actionIndex];
      const lastItem = lastId ? items.find(it => it.id === lastId) : null;
      const defaultSupplier = lastItem?.supplier || "";
      const newItem = await addItem(quoteId || id, {
        supplier: defaultSupplier, location: "Click and Collect", item_name: "", sku: "",
        unit_price: 0, qty: 1, product_url: "", ordered: false, action_index: actionIndex,
      });
      setItems(prev => [...prev, newItem]);
      lastAddedIdByScope.current[actionIndex] = newItem.id;
    } catch { toast.error("Failed to add item"); }
    finally { addingItem.current = false; }
  };

  const handleUpdateItem = async (itemId, field, value) => {
    setItems(prev => prev.map(item => item.id === itemId ? { ...item, [field]: value } : item));
    try {
      const updated = await updateItem(quoteId || id, itemId, { [field]: value });
      setItems(prev => prev.map(item => item.id === itemId ? updated : item));
    } catch { toast.error("Failed to update item"); }
  };

  const handleAddSupplier = async (name) => {
    try {
      const result = await addSupplier(name);
      setConfig(prev => ({ ...prev, suppliers: result.suppliers }));
    } catch (err) { console.error(err); }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteItem(quoteId || id, itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success("Item removed");
    } catch { toast.error("Failed to remove item"); }
  };

  const addMeasurement = () => updateDraft("measurements", [...draft.measurements, { _uid: `m_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, label: "", value: "", unit: "mm" }]);
  const updateMeasurement = (index, updated) => { const m = [...draft.measurements]; m[index] = updated; updateDraft("measurements", m); };
  const removeMeasurement = (index) => updateDraft("measurements", draft.measurements.filter((_, i) => i !== index));

  const addAction = () => updateDraft("actions", [...draft.actions, { title: "Maintenance", description: "", hours: 0, price: 0, is_sub: false }]);
  const addSubAction = (afterIndex) => {
    const a = [...draft.actions];
    a.splice(afterIndex + 1, 0, { description: "", hours: 0, is_sub: true });
    updateDraft("actions", a);
  };
  const updateAction = (index, field, value) => {
    const a = [...draft.actions];
    a[index] = { ...a[index], [field]: value };
    if (field === "hours" && !a[index].is_sub) {
      a[index].price = computeActionRate(value, draft.client_type, draft.locked_hourly_rate);
    }
    updateDraft("actions", a);
  };
  // ───────────────────────────── Remove Service (cascade delete with Undo)
  // Bug fix: removing a service must also delete all its materials and shift the
  // action_index of every other item that came after it, otherwise the financial
  // summary keeps charging for materials that no longer have a visible service card.
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState(null);
  const undoTimerRef = useRef(null);

  const removeAction = (index) => {
    const attachedItems = items.filter(it => it.action_index === index);
    if (attachedItems.length === 0) {
      // Labour-only service — confirmation not needed
      performRemoveAction(index);
    } else {
      setConfirmRemoveIdx(index);
    }
  };

  const performRemoveAction = async (index) => {
    setConfirmRemoveIdx(null);
    const targetId = quoteId || id;
    if (!targetId) {
      // Quote not yet persisted — just splice client-side
      updateDraft("actions", draft.actions.filter((_, i) => i !== index));
      return;
    }
    const removedAction = draft.actions[index];
    const attachedItems = items.filter(it => it.action_index === index);
    const shiftedItems = items.filter(it => typeof it.action_index === "number" && it.action_index > index);

    // 1) Optimistic UI: splice action + drop items at this idx + shift remaining indexes down
    const newActions = draft.actions.filter((_, i) => i !== index);
    const newItems = items
      .filter(it => it.action_index !== index)
      .map(it => (typeof it.action_index === "number" && it.action_index > index)
        ? { ...it, action_index: it.action_index - 1 }
        : it);
    setDraft(prev => ({ ...prev, actions: newActions }));
    setItems(newItems);

    // 2) Persist deletions + index shifts to the backend, then persist the actions array
    //    so the cascade survives a page reload (otherwise the DB still has N actions
    //    while remaining items have already been re-indexed, leaving the quote in an
    //    inconsistent state — root cause of the iteration_14 regression).
    try {
      await Promise.all([
        ...attachedItems.map(it => deleteItem(targetId, it.id).catch(() => null)),
        ...shiftedItems.map(it => updateItem(targetId, it.id, { action_index: it.action_index - 1 }).catch(() => null)),
      ]);
      await updateQuote(targetId, { actions: newActions });
    } catch (err) {
      console.error("Cascade delete failed", err);
    }

    // 3) Toast with Undo (only when at least one material was removed)
    if (attachedItems.length > 0) {
      if (undoTimerRef.current) { clearTimeout(undoTimerRef.current); undoTimerRef.current = null; }
      toast(
        `Service removed — “${removedAction?.title || "Service"}” and ${attachedItems.length} material item${attachedItems.length === 1 ? "" : "s"} deleted.`,
        {
          duration: 15000,
          action: {
            label: "Undo",
            onClick: () => undoRemoveAction(index, removedAction, attachedItems, shiftedItems, newActions),
          },
        }
      );
      undoTimerRef.current = setTimeout(() => { undoTimerRef.current = null; }, 15000);
    }
  };

  const undoRemoveAction = async (originalIndex, removedAction, removedItems, shiftedItems, postRemoveActions) => {
    const targetId = quoteId || id;
    if (!targetId || !removedAction) return;
    // 1) Restore action client-side at the original position + compute the array to PUT
    //    IMPORTANT: use postRemoveActions (captured at perform-time) instead of draft.actions
    //    because the toast onClick closure may pre-date the setDraft re-render, leaving
    //    draft.actions stale (still showing the pre-remove 3-item array) and producing
    //    a duplicated entry when we splice the removedAction back in.
    const baseActions = Array.isArray(postRemoveActions) ? postRemoveActions : draft.actions;
    const restoredActionsArr = (() => {
      const a = [...baseActions];
      const insertAt = Math.min(Math.max(originalIndex, 0), a.length);
      a.splice(insertAt, 0, removedAction);
      return a;
    })();
    setDraft(prev => {
      const a = [...(Array.isArray(postRemoveActions) ? postRemoveActions : prev.actions)];
      const insertAt = Math.min(Math.max(originalIndex, 0), a.length);
      a.splice(insertAt, 0, removedAction);
      return { ...prev, actions: a };
    });
    // 2) Re-create the removed items + shift the rest back up + persist the actions array
    try {
      const recreated = [];
      for (const it of removedItems) {
        const { id: _oldId, quote_id: _qid, ...rest } = it;
        const newItem = await addItem(targetId, { ...rest, action_index: originalIndex });
        recreated.push(newItem);
      }
      await Promise.all(shiftedItems.map(it => updateItem(targetId, it.id, { action_index: it.action_index }).catch(() => null)));
      await updateQuote(targetId, { actions: restoredActionsArr });
      setItems(prev => {
        const updatedExisting = prev.map(it => {
          const wasShifted = shiftedItems.find(s => s.id === it.id);
          return wasShifted ? { ...it, action_index: wasShifted.action_index } : it;
        });
        return [...updatedExisting, ...recreated];
      });
      toast.success("Service restored");
    } catch (err) {
      console.error("Undo failed", err);
      toast.error("Failed to restore service");
    }
  };

  const handleMediaChange = () => {
    const targetId = quoteId || id;
    if (targetId) getQuote(targetId).then(data => setMedia(data.media || []));
  };

  const toggleGroup = (idx) => setCollapsedGroups(p => ({ ...p, [idx]: !p[idx] }));

  const suppliers = config?.suppliers || ["Screwfix", "Toolstation", "Wickes", "B&Q", "Other"];
  const locations = config?.locations || ["Storage Unit", "Click and Collect", "Site Delivery"];
  const clientTypes = config?.client_types || ["Bairstow Eves", "Hamptons", "Private", "Gladstone", "Individual", "Keatons"];

  // Build action groups with their materials
  const actionGroups = useMemo(() => {
    const groups = [];
    let mainIdx = 0;
    draft.actions.forEach((act, i) => {
      if (typeof act !== "object") return;
      if (!act.is_sub) {
        mainIdx++;
        const groupItems = items.filter(it => it.action_index === i);
        const subs = [];
        let j = i + 1;
        while (j < draft.actions.length && draft.actions[j]?.is_sub) {
          subs.push({ ...draft.actions[j], _idx: j, _items: items.filter(it => it.action_index === j) });
          j++;
        }
        const price = act.price != null && act.price > 0 ? act.price : computeActionRate(act.hours || 0, draft.client_type, draft.locked_hourly_rate);
        groups.push({ mainNum: mainIdx, idx: i, act, subs, items: groupItems, price });
      }
    });
    return groups;
  }, [draft.actions, items, draft.client_type]);

  const unlinkedItems = items.filter(it => it.action_index == null || it.action_index < 0);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <div className={`${isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-6 pb-4"} sticky top-0 z-30`} style={{ background: "var(--bg)" }}>
        <div className={`flex ${isMobile ? "flex-col gap-2" : "items-center justify-between"}`}>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn" style={{ padding: "8px 16px", border: "1px solid #E5E7EB", borderRadius: "6px" }}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className={isMobile ? "text-base font-semibold" : "text-xl font-semibold"} style={{ color: "var(--text-primary)" }}>
              {isEdit ? "Edit Quote" : "New Quote"}
            </h1>
            {draft.client_type && (
              <span className="badge-pill badge-blue text-xs" data-testid="header-client-badge">{draft.client_type}</span>
            )}
          </div>
          <Button onClick={handleSave} disabled={saving} className={`btn-primary gap-2 ${isMobile ? "h-9 w-full" : "h-10 px-6"}`} data-testid="save-quote-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : (draft.works_type === "Works Order" && draft.status === "Quote Accepted") ? "Save Invoice" : "Save Quote"}
          </Button>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto ${isMobile ? "px-4 py-3" : "px-8 py-4"}`}>
        <div className={`grid grid-cols-1 ${isMobile ? "" : "lg:grid-cols-12"} gap-6`}>
          {/* Main content area */}
          <div className={isMobile ? "" : "lg:col-span-9"}>

            {/* Site Details */}
            <section className="ui-surface p-6 mb-6" data-testid="site-details-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-5" style={{ color: "var(--text-secondary)" }}>Site Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1.5 block">Client Type</Label>
                  <div className="h-10 rounded-lg flex items-center px-3 text-sm font-medium" style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-primary)" }} data-testid="client-type-display">
                    {draft.client_type}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1.5 block">Address</Label>
                  <Input value={draft.site_address} onChange={(e) => updateDraft("site_address", e.target.value)} placeholder="Full site address" className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="site-address-input" />
                </div>
              </div>
            </section>

            {/* Quote Visit Notes */}
            {quoteVisitNotes && (
              <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                <h2 className="text-sm font-semibold tracking-tight uppercase mb-2 text-amber-800">Quote Visit Notes</h2>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{quoteVisitNotes}</p>
              </section>
            )}

            {/* Job Details */}
            <section className="ui-surface p-6 mb-6" data-testid="job-details-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>
                  <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} />
                  Job Description
                </h2>
                <AiRewriteButton text={draft.job_details} onRewrite={(text) => updateDraft("job_details", text)} />
              </div>
              <textarea
                value={draft.job_details}
                onChange={(e) => updateDraft("job_details", e.target.value)}
                placeholder="Enter job details, scope of work, special instructions..."
                rows={5}
                className="w-full rounded-lg p-4 text-sm resize-y focus:outline-none transition-colors"
                style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
                data-testid="job-details-textarea"
              />
            </section>

            {/* Services / Actions */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>Services</h2>
                <div className="flex items-center gap-2">
                  <AiQuoteGenerator
                    clientType={draft.client_type}
                    jobDescription={draft.job_details}
                    photoUrls={(media || []).filter(m => m.category === "photo").map(m => m.storage_path)}
                    address={draft.site_address}
                    onApply={applyAiResult}
                  />
                  <Button onClick={addAction} className="btn-primary gap-1.5 h-9 px-4 text-sm" data-testid="add-action-btn">
                    <Plus className="w-4 h-4" /> Add Service
                  </Button>
                </div>
              </div>
              <div className="flex justify-end mb-4">
                <AiQuoteGenerator
                  clientType={draft.client_type}
                  jobDescription={draft.job_details}
                  photoUrls={(media || []).filter(m => m.category === "photo").map(m => m.storage_path)}
                  address={draft.site_address}
                  onApply={applyAiResult}
                  singleAction
                  triggerLabel="Generate single action"
                  testId="ai-single-action-link"
                />
              </div>

              {actionGroups.length === 0 ? (
                <div className="ui-surface p-12 text-center" style={{ color: "var(--text-muted)" }}>
                  <p className="text-sm">No services added yet. Click "Add Service" to describe the work.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {actionGroups.map(g => {
                    const collapsed = collapsedGroups[g.idx];
                    const groupMaterialTotal = g.items.reduce((s, it) => s + (it.unit_price || 0) * (it.qty || 0), 0);
                    return (
                      <div key={g.idx} className="ui-surface overflow-hidden" data-testid={`service-group-${g.mainNum}`}>
                        {/* Group Header */}
                        <div
                          className="flex items-center justify-between px-5 py-4 cursor-pointer select-none hover:bg-slate-100 transition-colors"
                          style={{ borderBottom: collapsed ? "none" : "1px solid var(--border)", background: "#F3F4F6" }}
                          onClick={() => toggleGroup(g.idx)}
                        >
                          <div className="flex items-center gap-3">
                            {collapsed ? <ChevronRight className="w-4 h-4" style={{ color: "var(--text-muted)" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
                            <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                              {g.mainNum}. {g.act.title || "Untitled Service"}
                            </span>
                            {g.act.hours > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
                                {g.act.hours >= 7 ? `${(g.act.hours / 7).toFixed(g.act.hours % 7 === 0 ? 0 : 1)} day${g.act.hours / 7 > 1 ? "s" : ""}` : `${g.act.hours} hrs`}
                              </span>
                            )}
                            {(g.act.contractors || 1) > 1 && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
                                {g.act.contractors} people
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono font-bold" style={{ color: "var(--text-primary)" }}>{formatCurrency(g.price)}</span>
                          </div>
                        </div>

                        {!collapsed && (
                          <div className="px-5 py-4">
                            {/* Action title & description */}
                            <div className="mb-5">
                              <Input
                                value={g.act.title || ""}
                                onChange={(e) => updateAction(g.idx, "title", e.target.value)}
                                placeholder="Service title"
                                className="h-11 rounded-lg text-base font-semibold mb-3"
                                style={{ border: "1px solid var(--border)" }}
                                data-testid={`action-title-${g.idx}`}
                              />
                              <div className="relative">
                                <textarea
                                  value={g.act.description || ""}
                                  onChange={(e) => updateAction(g.idx, "description", e.target.value)}
                                  placeholder="Describe the work to be done..."
                                  rows={4}
                                  className="w-full rounded-lg p-4 pr-28 text-sm resize-y focus:outline-none transition-colors"
                                  style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
                                  data-testid={`action-desc-${g.idx}`}
                                />
                                <div className="absolute top-2 right-2">
                                  <ServiceRewriteButton
                                    text={g.act.description || ""}
                                    onRewrite={(t) => updateAction(g.idx, "description", t)}
                                    serviceName={g.act.title || ""}
                                    jobDescription={draft.job_details || ""}
                                    clientType={draft.client_type || ""}
                                    testId={`service-ai-rewrite-${g.idx}`}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Duration, Contractors & Rate row */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
                              <div>
                                <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Duration</Label>
                                <ActionDurationSelect value={g.act.hours || 0} onChange={(v, isCallOut) => {
                                  const a = [...draft.actions];
                                  const contractors = a[g.idx].contractors || 1;
                                  a[g.idx] = { ...a[g.idx], hours: v, price: isCallOut ? CALL_OUT_RATE * contractors : computeActionRate(v, draft.client_type, draft.locked_hourly_rate) * contractors };
                                  updateDraft("actions", a);
                                }} index={g.idx} worksType={draft.works_type} clientType={draft.client_type} lockedRate={draft.locked_hourly_rate} />
                              </div>
                              <div>
                                <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Contractors</Label>
                                <Select value={String(g.act.contractors || 1)} onValueChange={(v) => {
                                  const a = [...draft.actions];
                                  const num = parseInt(v);
                                  const basePrice = computeActionRate(a[g.idx].hours || 0, draft.client_type, draft.locked_hourly_rate);
                                  a[g.idx] = { ...a[g.idx], contractors: num, price: basePrice * num };
                                  updateDraft("actions", a);
                                }}>
                                  <SelectTrigger className="h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "person" : "people"}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Rate</Label>
                                {editingRate[g.idx] ? (
                                  <Input type="number" step="0.01" min="0" autoFocus
                                    value={g.act.price ?? computeActionRate(g.act.hours || 0, draft.client_type, draft.locked_hourly_rate)}
                                    onChange={(e) => updateAction(g.idx, "price", parseFloat(e.target.value) || 0)}
                                    onBlur={() => setEditingRate(p => ({ ...p, [g.idx]: false }))}
                                    onKeyDown={(e) => e.key === "Enter" && setEditingRate(p => ({ ...p, [g.idx]: false }))}
                                    className="h-10 rounded-lg font-mono text-right" style={{ border: "1px solid var(--border)" }}
                                    data-testid={`action-price-${g.idx}`}
                                  />
                                ) : (
                                  <div className="h-10 rounded-lg flex items-center justify-end px-3 font-mono text-sm cursor-pointer hover:bg-slate-100 transition-colors"
                                    style={{ border: "1px solid var(--border)", background: "var(--bg)" }}
                                    onClick={() => {
                                      if (!g.act.price || g.act.price === 0) updateAction(g.idx, "price", computeActionRate(g.act.hours || 0, draft.client_type, draft.locked_hourly_rate));
                                      setEditingRate(p => ({ ...p, [g.idx]: true }));
                                    }}
                                    data-testid={`action-price-${g.idx}`}
                                  >
                                    {formatCurrency(g.price)}
                                  </div>
                                )}
                              </div>
                              <div>
                                <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Total</Label>
                                <div className="h-10 rounded-lg flex items-center justify-end px-3 font-mono text-sm font-bold" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                                  {formatCurrency(g.price)}
                                </div>
                                {(g.act.contractors || 1) > 1 && (
                                  <p className="text-[9px] mt-1 text-right" style={{ color: "var(--text-muted)" }}>
                                    {formatCurrency(g.price / (g.act.contractors || 1))}/person × {g.act.contractors}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-end gap-1">
                                <Button variant="outline" size="sm" onClick={() => addSubAction(g.idx)} className="h-10 rounded-lg text-xs gap-1 flex-1" style={{ border: "1px solid var(--border)" }} data-testid={`add-sub-action-${g.idx}`}>
                                  <Plus className="w-3 h-3" /> Sub-task
                                </Button>
                              </div>
                            </div>

                            {/* Sub-actions */}
                            {g.subs.map((sub, si) => (
                              <div key={sub._idx} className="ml-6 mb-4 pl-4" style={{ borderLeft: "3px solid #E5E7EB" }}>
                                <div className="flex items-start gap-3 mb-2">
                                  <span className="text-xs font-mono pt-2.5" style={{ color: "var(--text-muted)" }}>{g.mainNum}.{si + 1}</span>
                                  <div className="flex-1">
                                    <textarea
                                      value={sub.description || ""}
                                      onChange={(e) => updateAction(sub._idx, "description", e.target.value)}
                                      placeholder="Sub-task description..."
                                      rows={2}
                                      className="w-full rounded-lg p-3 text-sm resize-y focus:outline-none"
                                      style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}
                                      data-testid={`action-desc-${sub._idx}`}
                                    />
                                  </div>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 mt-1" style={{ color: "var(--text-muted)" }} onClick={() => removeAction(sub._idx)} data-testid={`remove-action-${sub._idx}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                                {/* Sub-action materials */}
                                {sub._items.length > 0 && (
                                  <div className="ml-8 mb-2">
                                    {sub._items.map(item => (
                                      <CompactMaterialRow key={item.id} item={item} suppliers={suppliers} locations={locations} onUpdate={handleUpdateItem} onDelete={handleDeleteItem} onAddSupplier={handleAddSupplier} />
                                    ))}
                                  </div>
                                )}
                                <button onClick={() => handleAddItem(sub._idx)} className="ml-8 text-xs font-medium flex items-center gap-1 py-1" style={{ color: "var(--primary)" }} data-testid={`add-action-material-${sub._idx}`}>
                                  <Plus className="w-3 h-3" /> Add Material
                                </button>
                              </div>
                            ))}

                            {/* Materials for this action */}
                            {g.items.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-[11px] font-semibold tracking-wide uppercase mb-2 flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}>
                                  <Package className="w-3 h-3" /> Materials
                                </h4>
                                <div className="rounded-lg" style={{ border: "1px solid var(--border)" }}>
                                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ background: "var(--bg)", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <div className="col-span-2">Supplier</div>
                                    <div className="col-span-3">Item</div>
                                    <div className="col-span-1">SKU</div>
                                    <div className="col-span-2">Location</div>
                                    <div className="col-span-1 text-right">Price</div>
                                    <div className="col-span-1 text-right">Qty</div>
                                    <div className="col-span-1 text-right">Total</div>
                                    <div className="col-span-1"></div>
                                  </div>
                                  {g.items.map((item) => (
                                    <CompactMaterialRow key={item.id} item={item} suppliers={suppliers} locations={locations} onUpdate={handleUpdateItem} onDelete={handleDeleteItem} onAddSupplier={handleAddSupplier} />
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Add material button + Remove action */}
                            <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                              <button onClick={() => handleAddItem(g.idx)} className="text-xs font-medium flex items-center gap-1 py-1" style={{ color: "var(--primary)" }} data-testid={`add-action-material-${g.idx}`}>
                                <Plus className="w-3 h-3" /> Add Material
                              </button>
                              {confirmRemoveIdx === g.idx ? (
                                <div className="flex items-center gap-2 text-xs" data-testid={`remove-confirm-${g.idx}`}>
                                  <span style={{ color: "var(--text-secondary)" }}>
                                    Remove this service and its {g.items.length} material item{g.items.length === 1 ? "" : "s"}? This cannot be undone.
                                  </span>
                                  <Button size="sm" className="h-8 gap-1" style={{ background: "#DC2626", color: "#fff" }} onClick={() => performRemoveAction(g.idx)} data-testid={`confirm-remove-${g.idx}`}>
                                    <Trash2 className="w-3 h-3" /> Confirm Remove
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirmRemoveIdx(null)} data-testid={`cancel-remove-${g.idx}`}>
                                    Keep Service
                                  </Button>
                                </div>
                              ) : (
                                <Button variant="ghost" size="sm" className="text-xs gap-1 h-8" style={{ color: "var(--danger)" }} onClick={() => removeAction(g.idx)} data-testid={`remove-action-${g.idx}`}>
                                  <Trash2 className="w-3 h-3" /> Remove Service
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* General / unlinked materials */}
              {unlinkedItems.length > 0 && (
                <div className="ui-surface overflow-hidden mt-4">
                  <div className="px-5 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
                    <span className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>General Materials</span>
                  </div>
                  <div className="px-5 py-3">
                    <div className="rounded-lg" style={{ border: "1px solid var(--border)" }}>
                      <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ background: "var(--bg)", color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                        <div className="col-span-2">Supplier</div>
                        <div className="col-span-3">Item</div>
                        <div className="col-span-1">SKU</div>
                        <div className="col-span-2">Location</div>
                        <div className="col-span-1 text-right">Price</div>
                        <div className="col-span-1 text-right">Qty</div>
                        <div className="col-span-1 text-right">Total</div>
                        <div className="col-span-1"></div>
                      </div>
                      {unlinkedItems.map(item => (
                        <CompactMaterialRow key={item.id} item={item} suppliers={suppliers} locations={locations} onUpdate={handleUpdateItem} onDelete={handleDeleteItem} onAddSupplier={handleAddSupplier} />
                      ))}
                    </div>
                    <button onClick={() => handleAddItem(-1)} className="text-xs font-medium flex items-center gap-1 py-2 mt-2" style={{ color: "var(--primary)" }} data-testid="add-item-btn">
                      <Plus className="w-3 h-3" /> Add Material
                    </button>
                  </div>
                </div>
              )}

              {/* Add action + unlinked material buttons */}
              <div className="flex items-center gap-3 mt-4">
                <Button onClick={addAction} variant="outline" className="gap-1.5 h-10 px-5 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="add-action-btn-bottom">
                  <Plus className="w-4 h-4" /> Add Service
                </Button>
                <Button onClick={() => handleAddItem(-1)} variant="outline" className="gap-1.5 h-10 px-5 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }}>
                  <Plus className="w-4 h-4" /> Add Material
                </Button>
              </div>
            </section>

            {/* Congestion Charge */}
            <section className="ui-surface p-6 mb-6" data-testid="congestion-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Congestion Charge</h2>
              {isInCongestionZone(draft.site_address) ? (
                <div>
                  <div className="flex items-center gap-3 p-3 rounded-lg mb-3" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
                    <span className="text-xs font-bold" style={{ color: "#92400E" }}>This address is in the London Congestion Charge Zone — £{CONGESTION_CHARGE}/day</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Number of Days</Label>
                      <Input type="number" step="1" min="0" value={draft.congestion_days || ""} onChange={(e) => updateDraft("congestion_days", parseInt(e.target.value) || 0)} placeholder="0" className="h-10 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} data-testid="congestion-days" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Total (inc VAT)</Label>
                      <div className="h-10 rounded-lg flex items-center px-3 font-mono text-sm font-bold" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
                        {formatCurrency((draft.congestion_days || 0) * CONGESTION_CHARGE)}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>Not applicable — address is outside the congestion zone</p>
              )}
            </section>

            {/* Measurements */}
            <section className="ui-surface p-6 mb-6" data-testid="measurements-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>
                  <Ruler className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Measurements
                </h2>
                <Button variant="outline" size="sm" onClick={addMeasurement} className="rounded-lg gap-1 text-xs h-9" style={{ border: "1px solid var(--border)" }} data-testid="add-measurement-btn">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {draft.measurements.length === 0 ? (
                <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>No measurements recorded</p>
              ) : (
                <div className="space-y-2">
                  {draft.measurements.map((m, i) => (
                    <MeasurementRow key={m._uid || `m-${i}`} measurement={m} index={i} onChange={updateMeasurement} onRemove={removeMeasurement} />
                  ))}
                </div>
              )}
            </section>

            {/* Additional Terms */}
            <section className="ui-surface p-6 mb-6" data-testid="terms-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} />
                Additional Terms
              </h2>
              <textarea
                value={draft.additional_terms || ""}
                onChange={(e) => updateDraft("additional_terms", e.target.value)}
                placeholder="Add any additional terms, conditions, or notes to include on the quote..."
                rows={4}
                className="w-full rounded-lg p-4 text-sm resize-y focus:outline-none transition-colors"
                style={{ border: "1px solid var(--border)", color: "var(--text-primary)" }}
                data-testid="additional-terms"
              />
            </section>

            {/* Parking */}
            <section className="ui-surface p-6 mb-6" data-testid="parking-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Parking</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Parking Type</Label>
                  <Select value={draft.parking_type || ""} onValueChange={(v) => updateDraft("parking_type", v)}>
                    <SelectTrigger className="h-10 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="parking-type"><SelectValue placeholder="Select parking type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pay by Phone">Pay by Phone</SelectItem>
                      <SelectItem value="Residents Visitor Permit">Residents Visitor Permit</SelectItem>
                      <SelectItem value="Paid Car Park">Paid Car Park</SelectItem>
                      <SelectItem value="Concierge">Concierge</SelectItem>
                      <SelectItem value="Free on site/road">Free on site/road</SelectItem>
                      <SelectItem value="Dartford Crossing">Dartford Crossing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Hours</Label>
                    <Input type="number" step="0.5" min="0" value={draft.parking_hours || ""} onChange={(e) => updateDraft("parking_hours", parseFloat(e.target.value) || 0)} placeholder="0" className="h-10 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} data-testid="parking-hours" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium tracking-wide text-slate-400 uppercase mb-1.5 block">Rate (inc VAT)</Label>
                    <Input type="number" step="0.01" min="0" value={draft.parking_rate || ""} onChange={(e) => updateDraft("parking_rate", parseFloat(e.target.value) || 0)} placeholder="0.00" className="h-10 rounded-lg font-mono" style={{ border: "1px solid var(--border)" }} data-testid="parking-rate" />
                  </div>
                </div>
              </div>
            </section>

            {/* Access */}
            <section className="ui-surface p-6 mb-6" data-testid="access-section">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>Access</h2>
              <div className="space-y-4">
                <div className="flex gap-2">
                  {[
                    { value: "keys_branch", label: "Keys: Collect from Branch" },
                    { value: "occupier", label: "Access via Occupier" },
                    { value: "other", label: "Other / See Notes" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => updateDraft("access_method", draft.access_method === opt.value ? "" : opt.value)}
                      className="flex-1 py-2.5 px-3 rounded-lg text-xs font-medium text-center transition-colors"
                      style={draft.access_method === opt.value
                        ? { background: "#EFF6FF", color: "#1D4ED8", border: "2px solid #2680C2" }
                        : { background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }
                      }
                    >{opt.label}</button>
                  ))}
                </div>
                {draft.access_method === "keys_branch" && (
                  <div>
                    <Input value={draft.access_branch_address} onChange={(e) => updateDraft("access_branch_address", e.target.value)} placeholder="e.g. Bairstow Eves, 123 High Street, London, E1 4AB" className="h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
                    <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>Collect keys before attending the property.</p>
                  </div>
                )}
                {draft.access_method === "occupier" && (
                  <div>
                    <Input value={draft.access_occupier_phone || draft.tenant_phone || ""} onChange={(e) => updateDraft("access_occupier_phone", e.target.value)} placeholder="Enter occupier contact number" className="h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
                    {!draft.access_occupier_phone && draft.tenant_phone ? (
                      <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Pulled from job record</p>
                    ) : draft.access_occupier_phone && draft.access_occupier_phone !== draft.tenant_phone ? (
                      <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Edited</p>
                    ) : null}
                    <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>Tenant or occupier will provide access on arrival.</p>
                  </div>
                )}
                {draft.access_method === "other" && (
                  <div>
                    <textarea value={draft.access_notes} onChange={(e) => updateDraft("access_notes", e.target.value)} placeholder="Describe how to access the property..." className="w-full text-sm rounded-lg p-3 resize-y" style={{ border: "1px solid var(--border)", minHeight: "80px" }} />
                  </div>
                )}
              </div>
            </section>

            {/* Media — Job Photos */}
            <section className="ui-surface p-6 mb-6" data-testid="media-section-photos">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <ImageIcon className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Job Photos
              </h2>
              <FileUploadZone quoteId={quoteId || id} media={media} onMediaChange={handleMediaChange} category="photo" />
            </section>

            {/* Media — Material Purchases */}
            <section className="ui-surface p-6 mb-6" data-testid="media-section-receipts">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Material Purchase Receipts
              </h2>
              <FileUploadZone quoteId={quoteId || id} media={media} onMediaChange={handleMediaChange} category="receipt" />
            </section>

            {/* Media — Documents (internal only) */}
            <section className="ui-surface p-6 mb-6" data-testid="media-section-documents">
              <h2 className="text-sm font-semibold tracking-wide uppercase mb-4" style={{ color: "var(--text-secondary)" }}>
                <FileText className="w-4 h-4 inline mr-2" style={{ color: "var(--text-muted)" }} /> Documents
              </h2>
              <FileUploadZone quoteId={quoteId || id} media={media} onMediaChange={handleMediaChange} category="document" />
            </section>
          </div>

          {/* Sidebar — Financial Summary */}
          <div className={isMobile ? "" : "lg:col-span-3"}>
            <div className={isMobile ? "" : "sticky top-24"}>
              {/* Totals panel */}
              <div className="ui-surface overflow-hidden" data-testid="live-total-widget">
                <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border)" }}>
                  <h3 style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-secondary)" }}>Financial Summary</h3>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <SummaryRow label="Net amount" value={totals.totalExVat} bold />

                  <div className="pt-2">
                    <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>Costs breakdown</p>
                    <SummaryRow label="Materials (you paid inc VAT)" value={totals.materialsSubtotal} muted />
                    <SummaryRow label="Materials (on quote)" value={totals.materialsExVat} />
                    <SummaryRow label="Labour costs" value={totals.labourTotal} muted />
                    {(draft.parking_hours > 0 && draft.parking_rate > 0) && (
                      <>
                        <SummaryRow label="Parking (inc VAT entered)" value={totals.parkingIncVat} muted />
                        <SummaryRow label="Parking (on quote)" value={totals.parkingExVat} />
                      </>
                    )}
                    {totals.congestionDays > 0 && (
                      <>
                        <SummaryRow label={`Congestion (${totals.congestionDays}d × £${CONGESTION_CHARGE})`} value={totals.congestionIncVat} muted />
                        <SummaryRow label="Congestion (on quote)" value={totals.congestionExVat} />
                      </>
                    )}
                  </div>

                  {/* Materials markup - Private clients only */}
                  {getPricing(draft.client_type).materials_rule === "private" && (
                    <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>Materials Markup</p>
                      <div className="flex gap-1.5">
                        {[0, 5, 10, 15, 20].map(pct => (
                          <button key={pct} onClick={() => updateDraft("materials_markup", pct)}
                            className="flex-1 py-1.5 rounded-lg text-xs font-semibold text-center transition-colors"
                            style={draft.materials_markup === pct
                              ? { background: "var(--primary)", color: "#fff" }
                              : { background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }
                            }
                            data-testid={`markup-${pct}`}
                          >{pct}%</button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2" style={{ borderTop: "1px solid var(--border)" }}>
                    <SummaryRow label="Subtotal Ex VAT" value={totals.totalExVat} />
                    {draft.vat_enabled !== false && (
                      <SummaryRow label={`VAT (${FIXED_VAT_RATE}%)`} value={totals.vatAmount} muted />
                    )}
                  </div>

                  <div className="pt-3" style={{ borderTop: "2px solid var(--primary)" }}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Grand Total</span>
                      <span className="text-2xl font-mono font-bold" style={{ color: "var(--primary)" }} data-testid="grand-total">
                        {formatCurrency(totals.totalIncVat)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote Info */}
              <div className="ui-surface p-5 mt-4">
                <h4 className="text-xs font-semibold tracking-wide uppercase mb-3" style={{ color: "var(--text-muted)" }}>Quote Info</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Client Type</span><span className="font-medium">{draft.client_type}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Rate</span><span className="font-mono">{formatCurrency(draft.locked_hourly_rate || getPricing(draft.client_type).hourly_rate)}/h</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Items</span><span className="font-mono">{items.length}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Services</span><span className="font-mono">{actionGroups.length}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Media</span><span className="font-mono">{media.length}</span></div>
                </div>

                {/* VAT toggle */}
                <div className="flex items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                  <Switch checked={draft.vat_enabled} onCheckedChange={(v) => updateDraft("vat_enabled", v)} data-testid="vat-toggle" />
                  <Label className="text-sm">VAT Enabled</Label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function SummaryRow({ label, value, bold, muted }) {
  return (
    <div className="flex justify-between items-center py-0.5">
      <span className={`text-sm ${muted ? "" : bold ? "font-semibold" : ""}`} style={{ color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{label}</span>
      <span className={`font-mono text-sm ${bold ? "font-bold" : ""}`} style={{ color: muted ? "var(--text-muted)" : "var(--text-primary)" }}>{formatCurrency(value)}</span>
    </div>
  );
}


function ActionDurationSelect({ value, onChange, index, worksType, clientType, lockedRate }) {
  const pricing = getPricing(clientType);
  const rate = (parseFloat(lockedRate) || 0) > 0 ? parseFloat(lockedRate) : (pricing.hourly_rate || 54);
  const isCallOut = worksType === "Call Out";

  const getLabel = (h) => {
    if (!h || h === 0) return "No duration";
    if (h === 1 && isCallOut) return "1 hr — Call Out";
    if (h % HOURS_PER_DAY === 0 && h >= HOURS_PER_DAY) return `${h / HOURS_PER_DAY} day${h / HOURS_PER_DAY > 1 ? "s" : ""}`;
    if (h >= HOURS_PER_DAY) return `${(h / HOURS_PER_DAY).toFixed(1)} days`;
    return `${h} hrs`;
  };

  const standardValues = ["0","0.5","1","1.5","2","2.5","3","3.5","4","4.5","5","5.5","6","6.5","7","7.5","8","12","16","20","24","28","32","36","40"];
  const isCustom = value > 0 && !standardValues.includes(String(value));
  const displayValue = value > 0 ? String(value) : "0";

  return (
    <div>
      <Select value={isCustom ? "__custom_set__" : displayValue} onValueChange={(v) => {
        if (v === "__custom__" || v === "__custom_set__") {
          const days = prompt("Enter number of days:");
          if (days) onChange(parseFloat(days) * HOURS_PER_DAY, false);
        } else if (v === "__callout__") {
          onChange(1, true);
        } else {
          onChange(parseFloat(v), false);
        }
      }}>
        <SelectTrigger className="h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid={`action-hours-${index}`}>
          {isCustom ? <span>{getLabel(value)}</span> : <SelectValue placeholder="Duration" />}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">No duration</SelectItem>
          {isCallOut && <SelectItem value="__callout__">1 hr — Call Out (£80 fixed)</SelectItem>}
          <SelectItem value="0.5">0.5 hrs (£{(0.5 * rate).toFixed(2)})</SelectItem>
          <SelectItem value="1">1 hr (£{rate.toFixed(2)})</SelectItem>
          <SelectItem value="1.5">1.5 hrs</SelectItem>
          <SelectItem value="2">2 hrs</SelectItem>
          <SelectItem value="2.5">2.5 hrs</SelectItem>
          <SelectItem value="3">3 hrs</SelectItem>
          <SelectItem value="3.5">3.5 hrs</SelectItem>
          <SelectItem value="4">4 hrs</SelectItem>
          <SelectItem value="4.5">4.5 hrs</SelectItem>
          <SelectItem value="5">5 hrs</SelectItem>
          <SelectItem value="5.5">5.5 hrs</SelectItem>
          <SelectItem value="6">6 hrs</SelectItem>
          <SelectItem value="6.5">6.5 hrs</SelectItem>
          <SelectItem value="7">7 hrs</SelectItem>
          <SelectItem value="7.5">7.5 hrs</SelectItem>
          <SelectItem value="8">1 day (8 hrs)</SelectItem>
          <SelectItem value="12">1.5 days</SelectItem>
          <SelectItem value="16">2 days</SelectItem>
          <SelectItem value="20">2.5 days</SelectItem>
          <SelectItem value="24">3 days</SelectItem>
          <SelectItem value="28">3.5 days</SelectItem>
          <SelectItem value="32">4 days</SelectItem>
          <SelectItem value="36">4.5 days</SelectItem>
          <SelectItem value="40">5 days</SelectItem>
          {isCustom && <SelectItem value="__custom_set__">{getLabel(value)} (custom)</SelectItem>}
          <SelectItem value="__custom__">Enter days...</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}


function CompactMaterialRow({ item, suppliers, locations, onUpdate, onDelete, onAddSupplier }) {
  const [catResults, setCatResults] = useState([]);
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const catTimer = useRef(null);
  const itemInputRef = useRef(null);

  const [localItemName, setLocalItemName] = useState(item.item_name || "");

  const searchCatalogue = (query) => {
    if (catTimer.current) clearTimeout(catTimer.current);
    if (query.length < 2) { setCatResults([]); setShowCatDropdown(false); return; }
    catTimer.current = setTimeout(async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/materials-catalogue?supplier=${encodeURIComponent(item.supplier || "")}&search=${encodeURIComponent(query)}`);
        const data = await resp.json();
        setCatResults(data.sort((a, b) => (a.item_name || "").localeCompare(b.item_name || "")));
        setShowCatDropdown(true);
      } catch { setCatResults([]); setShowCatDropdown(false); }
    }, 400);
  };

  const selectCatItem = (c) => {
    setLocalItemName(c.item_name);
    onUpdate(item.id, "item_name", c.item_name);
    onUpdate(item.id, "sku", c.sku || "");
    onUpdate(item.id, "unit_price", c.unit_price || 0);
    setCatResults([]);
    setShowCatDropdown(false);
  };

  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-start hover:bg-slate-50 transition-colors" style={{ borderBottom: "1px solid var(--border)" }} data-testid={`item-row-${item.id}`}>
      <div className="col-span-2">
        <SupplierSelect
          value={item.supplier}
          suppliers={suppliers}
          onChange={(name) => { onUpdate(item.id, "supplier", name); setCatResults([]); setShowCatDropdown(false); }}
          onAddSupplier={onAddSupplier}
          triggerClassName="h-8 rounded text-xs"
          testId={`supplier-select-${item.id}`}
        />
      </div>
      <div className="col-span-3 relative">
        <textarea ref={itemInputRef} value={localItemName} onChange={(e) => { setLocalItemName(e.target.value); searchCatalogue(e.target.value); }} onBlur={() => { if (localItemName !== item.item_name) onUpdate(item.id, "item_name", localItemName); setTimeout(() => { setShowCatDropdown(false); setCatResults([]); }, 150); }} onFocus={(e) => { if (e.target.value.length >= 2) searchCatalogue(e.target.value); }} onKeyDown={(e) => { if (e.key === "Escape") { setShowCatDropdown(false); setCatResults([]); } }} placeholder="Item description" rows={1} autoComplete="nope" className="w-full min-h-[32px] rounded text-sm p-1.5 resize-y focus:outline-none focus:ring-1" style={{ border: "1px solid var(--border)", fieldSizing: "content" }} />
        {showCatDropdown && catResults.length > 0 && (
          <div className="absolute z-[100] top-full left-0 mt-1 rounded-lg shadow-xl max-h-48 overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--border)", minWidth: "300px" }}>
            {catResults.map(c => (
              <button key={c.id} onMouseDown={(e) => e.preventDefault()} onClick={() => selectCatItem(c)} className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors" style={{ borderBottom: "1px solid var(--border)" }}>
                <span className="font-medium">{c.item_name}</span>
                {c.sku && <span style={{ color: "var(--text-muted)" }}> — {c.sku}</span>}
                <span className="font-mono ml-2" style={{ color: "var(--primary)" }}>{formatCurrency(c.unit_price)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="col-span-1">
        <textarea value={item.sku || ""} onChange={(e) => onUpdate(item.id, "sku", e.target.value)} placeholder="SKU" rows={1} autoComplete="nope" className="w-full min-h-[32px] rounded text-xs font-mono p-1.5 resize-y focus:outline-none focus:ring-1" style={{ border: "1px solid var(--border)", fieldSizing: "content" }} />
      </div>
      <div className="col-span-2">
        <Select value={item.location || "Click and Collect"} onValueChange={(v) => onUpdate(item.id, "location", v)}>
          <SelectTrigger className="h-8 rounded text-xs" style={{ border: "1px solid var(--border)" }}><SelectValue /></SelectTrigger>
          <SelectContent>{locations.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="col-span-1">
        <Input type="number" step="0.01" value={item.unit_price || ""} onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }} onChange={(e) => onUpdate(item.id, "unit_price", parseFloat(e.target.value) || 0)} onBlur={(e) => { if (!e.target.value) e.target.value = "0"; }} className="h-8 rounded text-sm font-mono text-right" style={{ border: "1px solid var(--border)" }} />
      </div>
      <div className="col-span-1">
        <QtySelect value={item.qty || 1} onChange={(v) => onUpdate(item.id, "qty", v)} />
      </div>
      <div className="col-span-1 text-right font-mono text-sm font-bold pr-1 pt-1.5">{formatCurrency((item.unit_price || 0) * (item.qty || 0))}</div>
      <div className="col-span-1 flex justify-end pt-1">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" style={{ color: "var(--text-muted)" }} onClick={() => onDelete(item.id)}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}


function QtySelect({ value, onChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const inputRef = useRef(null);
  const presets = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25, 50, 100];
  const isPreset = presets.includes(Number(value));

  useEffect(() => {
    if (showCustom && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustom]);

  if (showCustom) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.1"
        min="0.1"
        value={customVal}
        placeholder="Enter qty"
        onChange={(e) => setCustomVal(e.target.value)}
        onBlur={() => { const v = parseFloat(customVal); onChange(!v || v <= 0 ? 1 : v); setShowCustom(false); setCustomVal(""); }}
        onKeyDown={(e) => { if (e.key === "Enter") { const v = parseFloat(customVal); onChange(!v || v <= 0 ? 1 : v); setShowCustom(false); setCustomVal(""); } }}
        className="h-8 w-full rounded text-sm font-mono text-right px-2"
        style={{ border: "1px solid var(--primary)" }}
      />
    );
  }

  return (
    <Select value={isPreset ? String(value) : "__custom_set__"} onValueChange={(v) => {
      if (v === "__custom__" || v === "__custom_set__") { setCustomVal(""); setShowCustom(true); }
      else onChange(parseFloat(v));
    }}>
      <SelectTrigger className="h-8 rounded text-sm font-mono text-right" style={{ border: "1px solid var(--border)" }}>
        {isPreset ? <SelectValue /> : <span>{value}</span>}
      </SelectTrigger>
      <SelectContent>
        {presets.map(p => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}
        {!isPreset && <SelectItem value="__custom_set__">{value} (custom)</SelectItem>}
        <SelectItem value="__custom__"><span style={{ color: "var(--primary)" }}>+ Custom amount</span></SelectItem>
      </SelectContent>
    </Select>
  );
}
